import type { CareerStats, RoomView, SeatView, TableView } from '@rummy/protocol';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { sounds } from '../audio/sfx.ts';
import { Celebration } from '../components/Celebration.tsx';
import { HandHistory } from '../components/HandHistory.tsx';
import { Portrait } from '../components/Portrait.tsx';
import { Segmented } from '../components/Segmented.tsx';
import { computeSessionStats, type PlayerStats, type SessionStats } from '../game/sessionStats.ts';
import {
  nextAchievementProgress,
  readPersonalStats,
  recordPersonalSession,
  type PersonalStats,
} from '../game/personalStats.ts';
import { createSessionRecap, createSessionRecapPng } from '../game/sessionRecap.ts';
import { useCountUp } from '../hooks/useCountUp.ts';
import { useI18n, type MessageKey } from '../i18n/index.tsx';
import type { GameClient } from '../net/client.ts';

export interface SessionCompleteProps {
  table: TableView;
  room: RoomView;
  client: GameClient;
  accountId: string;
  onLeave: () => void;
  sound?: boolean;
  careerStats?: CareerStats;
}

type Tab = 'standings' | 'progress' | 'hands';

/**
 * §4.5 / §6.1 screen 6 — the result, what it was made of, and two buttons.
 *
 * The scoreboard used to be a two-column table, which is accurate and says
 * nothing: six hands of decisions collapse into one number per player. The
 * session's own history is already on the client, so the same screen now shows
 * how the lead actually moved, what each player's session looked like, and who
 * did the one thing worth naming — without asking the server for anything new.
 *
 * Rematch is still the retention mechanic. Nothing else needs to be, which is
 * why there is no "play again with someone new", no daily streak, and no reason
 * to stay on this screen once the group has decided.
 */
export function SessionComplete({
  table,
  room,
  client,
  accountId,
  onLeave,
  sound = false,
  careerStats,
}: SessionCompleteProps) {
  const { t, formatNumber } = useI18n();
  const [shared, setShared] = useState(false);
  const [tab, setTab] = useState<Tab>('standings');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [fire, setFire] = useState(0);
  const [detailsOpen, setDetailsOpen] = useState(
    () => !window.matchMedia('(max-width: 640px), (pointer: coarse)').matches,
  );
  const [personal, setPersonal] = useState<PersonalStats>(readPersonalStats);
  // Nothing is thrown under reduced motion, so the button that throws it again
  // would be a control that does nothing.
  const [animates] = useState(
    () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  const session = table.session;
  const isHost = room.hostId === accountId;

  const seatIds = useMemo(() => table.seats.map((s) => s.id), [table.seats]);
  const stats = useMemo(
    () => (session ? computeSessionStats(session, seatIds) : null),
    [session, seatIds],
  );
  const bySeat = useMemo(
    () => new Map(table.seats.map((s) => [s.id, s] as const)),
    [table.seats],
  );

  const youWon = session?.winners.includes(accountId) ?? false;
  const complete = session?.complete ?? false;
  const career = careerStats ?? personal;
  // A record of what already happened is not a reason to play the next
  // hand; a near-miss is. Only surfaced once it is genuinely close (§ above).
  const nextAchievement = useMemo(() => nextAchievementProgress(personal), [personal]);

  // One volley when the screen arrives, and only then — a reconnect, a rematch
  // vote or a settings change must not set it off again.
  const fired = useRef(false);
  useEffect(() => {
    if (!complete || fired.current) return;
    fired.current = true;
    setFire((n) => n + 1);
    if (!sound || !youWon) return;
    sounds.play('win');
  }, [complete, youWon, sound]);

  useEffect(() => {
    if (!session?.complete) return;
    setPersonal(recordPersonalSession(room.id, session, accountId));
  }, [room.id, session, accountId]);

  if (!session || !stats) return null;

  const winners = session.winners.map((id) => bySeat.get(id)?.handle ?? id);
  const ordered = stats.players;
  // "Deals · 6 hands" already says how many were played; a pool ends on
  // standings, so there the count is news.
  const formatLabel = t(`format.${session.format}` as MessageKey);
  const line = session.format.startsWith('pool')
    ? `${room.label} · ${formatLabel} · ${t('session.hands', { n: stats.hands })}`
    : `${room.label} · ${formatLabel}`;

  const summaryText = [
    line,
    ...ordered.map(
      (p) => `${bySeat.get(p.id)?.handle ?? p.id}  ${formatNumber(p.score)}`,
    ),
  ].join('\n');

  const shareRecap = async () => {
    const recapRows = ordered.map((player) => ({
      name: bySeat.get(player.id)?.handle ?? player.id,
      score: formatNumber(player.score),
      winner: session.winners.includes(player.id),
    }));
    const subtitle = `${formatLabel} · ${t('session.hands', { n: stats.hands })}`;
    // PNG first — it is the format share sheets and chat apps actually
    // render everywhere. The SVG only steps in if rasterizing fails.
    const png = await createSessionRecapPng(room.label, subtitle, recapRows);
    const file = png ?? createSessionRecap(room.label, subtitle, recapRows);
    try {
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ title: room.label, text: summaryText, files: [file] });
      } else {
        await navigator.clipboard.writeText(summaryText);
      }
      setShared(true);
      setTimeout(() => setShared(false), 1800);
    } catch {
      /* A cancelled platform sheet is not an error worth surfacing. */
    }
  };

  return (
    <main className="complete">
      <Celebration fire={fire} restrained={!youWon} />

      <section className={`finale${youWon ? ' finale--yours' : ''}`}>
        <p className="finale__eyebrow">{line}</p>
        <h1 className="finale__title">
          {winners.length === 1
            ? t('session.winner', { name: winners[0]! })
            : t('session.winner.tie')}
        </h1>
        <p className="finale__margin">
          {stats.margin !== null
            ? t('session.margin', { points: stats.margin })
            : t('session.margin.none')}
        </p>

        <Podium players={ordered} seats={bySeat} accountId={accountId} />

        {youWon && animates && (
          <button
            type="button"
            className="btn btn--ghost btn--small finale__again"
            onClick={() => setFire((n) => n + 1)}
          >
            {t('session.celebrate')}
          </button>
        )}
      </section>

      <button
        type="button"
        className="btn complete__details-toggle"
        aria-expanded={detailsOpen}
        onClick={() => setDetailsOpen((open) => !open)}
      >
        {detailsOpen ? t('session.details.hide') : t('session.details.show')}
      </button>

      {detailsOpen && <div className="complete__details">
      <button
        type="button"
        className="btn btn--small complete__details-close"
        onClick={() => setDetailsOpen(false)}
      >
        {t('session.details.hide')}
      </button>
      {stats.awards.length > 0 && (
        <ul className="awards">
          {stats.awards.map((award) => {
            const seat = bySeat.get(award.player);
            return (
              <li key={award.key} className="award">
                <span className="award__label">
                  {t(`session.award.${award.key}` as MessageKey)}
                </span>
                <span className="award__who">
                  <Portrait character={seat?.character ?? null} handle={seat?.handle ?? ''} size={26} />
                  <span className="award__name">{seat?.handle ?? award.player}</span>
                </span>
                <span className="award__value">
                  {award.key === 'wins'
                    ? t('session.award.wins.value', { n: award.value })
                    : t('session.award.points.value', {
                        points: Math.round(award.value * 10) / 10,
                      })}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {/* A losing run is where a player quietly stops opening the app. A
          supportive line here — not a popup, not a pause — is the entire
          intervention: no judgment, no red, just an offer of a lower-stakes
          table (§6.3's tone applies here too). */}
      {personal.lossStreak >= 3 && (
        <p className="tilt-nudge panel" role="status">
          {t('session.tiltNudge', { n: personal.lossStreak })}
        </p>
      )}

      <section className="personal-stats panel" aria-label={t('personal.heading')}>
        <div className="personal-stats__head">
          <div>
            <p className="panel__title">{t('personal.eyebrow')}</p>
            <h2>{t('personal.heading')}</h2>
          </div>
          <span>{careerStats ? t('personal.account') : t('personal.device')}</span>
        </div>
        <div className="personal-stats__grid">
          <div><strong>{career.sessions}</strong><span>{t('personal.sessions')}</span></div>
          <div><strong>{career.hands === 0 ? '—' : `${Math.round((career.wins / career.hands) * 100)}%`}</strong><span>{t('personal.winRate')}</span></div>
          <div><strong>{career.hands === 0 ? '—' : Math.round(career.totalPoints / career.hands)}</strong><span>{t('personal.avgPoints')}</span></div>
          <div><strong>{career.drops}</strong><span>{t('personal.drops')}</span></div>
        </div>
      </section>

      {(personal.achievements.length > 0 || nextAchievement) && (
        <section className="private-achievements panel" aria-label={t('achievement.heading')}>
          <div>
            <p className="panel__title">{t('achievement.eyebrow')}</p>
            <h2>{t('achievement.heading')}</h2>
          </div>
          {personal.achievements.length > 0 && (
            <ul className="private-achievements__list">
              {personal.achievements.map((achievement) => (
                <li key={achievement} className="private-achievement">
                  <span aria-hidden="true">{achievement === 'calm-table' ? '◌' : '✦'}</span>
                  <span>
                    <strong>{t(`achievement.${achievement}.title` as MessageKey)}</strong>
                    <small>{t(`achievement.${achievement}.copy` as MessageKey)}</small>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {nextAchievement && (
            <p className="private-achievements__next">
              {t('achievement.next', {
                remaining: nextAchievement.remaining,
                title: t(`achievement.${nextAchievement.achievement}.title` as MessageKey),
              })}
            </p>
          )}
        </section>
      )}

      <div className="panel complete__panel">
        <Segmented<Tab>
          value={tab}
          onChange={setTab}
          label={t('session.byHand')}
          even
          options={[
            { value: 'standings', label: t('session.tab.standings') },
            { value: 'progress', label: t('session.tab.progress') },
            { value: 'hands', label: t('session.tab.hands') },
          ]}
        />

        <div className="complete__tab">
          {tab === 'standings' && (
            <StandingsBoard stats={stats} seats={bySeat} accountId={accountId} />
          )}
          {tab === 'progress' && (
            <ScoreChart stats={stats} seats={bySeat} accountId={accountId} />
          )}
          {tab === 'hands' && (
            <>
              <HandGrid stats={stats} seats={bySeat} accountId={accountId} />
              {/* The grid is the points. This is the cards behind them — the
                  question people actually ask once the session is over. */}
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setHistoryOpen(true)}
              >
                {t('table.history')}
              </button>
            </>
          )}
        </div>
      </div>
      </div>}

      {historyOpen && (
        <HandHistory
          session={session}
          seats={table.seats}
          accountId={accountId}
          fallbackRules={table.rules}
          onClose={() => setHistoryOpen(false)}
        />
      )}

      <div className="complete__actions">
        <button
          type="button"
          className="btn btn--primary btn--large"
          disabled={!isHost}
          onClick={() => client.rematch()}
        >
          {t('session.rematch')}
        </button>
        <button type="button" className="btn btn--large" onClick={onLeave}>
          {t('session.leave')}
        </button>
        <button
          type="button"
          className={`btn btn--ghost${shared ? ' btn--done' : ''}`}
          onClick={() => void shareRecap()}
        >
          {shared ? t('session.shared') : t('session.share')}
        </button>
      </div>
    </main>
  );
}

/* ------------------------------------------------------------------ podium */

interface BoardProps {
  stats: SessionStats;
  seats: Map<string, SeatView>;
  accountId: string;
}

/**
 * Top three, in the arrangement everyone already knows how to read: winner
 * centre and tallest. Below three players it degrades to whoever there is,
 * which is the honest shape of a two-handed session.
 */
function Podium({
  players,
  seats,
  accountId,
}: {
  players: PlayerStats[];
  seats: Map<string, SeatView>;
  accountId: string;
}) {
  const top = players.slice(0, 3);
  // 2nd, 1st, 3rd — the plinth order, not the ranking order.
  const arranged = top.length >= 3 ? [top[1]!, top[0]!, top[2]!] : top.length === 2 ? [top[1]!, top[0]!] : top;
  const HEIGHT: Record<number, number> = { 1: 88, 2: 62, 3: 46 };

  return (
    <ol className="podium">
      {arranged.map((player) => {
        const seat = seats.get(player.id);
        const place = Math.min(player.rank, 3);
        return (
          <li
            key={player.id}
            className={`podium__slot podium__slot--${place}${player.id === accountId ? ' is-you' : ''}`}
            style={{ '--plinth': `${HEIGHT[place] ?? 46}px` } as React.CSSProperties}
          >
            <Portrait
              character={seat?.character ?? null}
              handle={seat?.handle ?? player.id}
              size={place === 1 ? 60 : 46}
              dim={player.eliminated}
            />
            <span className="podium__name">{seat?.handle ?? player.id}</span>
            <PodiumScore value={player.score} place={place} />
            <span className="podium__plinth" aria-hidden="true">
              <span className="podium__place">{player.rank}</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function PodiumScore({ value, place }: { value: number; place: number }) {
  const { formatNumber } = useI18n();
  const shown = useCountUp(value, { duration: 1000, delay: 220 + place * 90 });
  return <span className="podium__score">{formatNumber(shown)}</span>;
}

/* -------------------------------------------------------------- standings */

/**
 * The scoreboard, with each row openable. The bar is the same number as the
 * figure beside it — it exists so the gaps are visible at a glance, which a
 * column of digits never makes them.
 */
function StandingsBoard({ stats, seats, accountId }: BoardProps) {
  const { t, formatNumber } = useI18n();
  const [open, setOpen] = useState<string | null>(null);
  const worst = Math.max(1, ...stats.players.map((p) => p.score));

  return (
    <ul className="board">
      {stats.players.map((player) => {
        const seat = seats.get(player.id);
        const expanded = open === player.id;
        return (
          <li
            key={player.id}
            className={[
              'board__row',
              player.rank === 1 ? 'is-winner' : '',
              player.eliminated ? 'is-out' : '',
              player.id === accountId ? 'is-you' : '',
              expanded ? 'is-open' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <button
              type="button"
              className="board__head"
              aria-expanded={expanded}
              onClick={() => setOpen(expanded ? null : player.id)}
            >
              <span className="board__rank">{player.rank}</span>
              <Portrait
                character={seat?.character ?? null}
                handle={seat?.handle ?? player.id}
                size={34}
                dim={player.eliminated}
              />
              <span className="board__name">
                {seat?.handle ?? player.id}
                {player.id === accountId && <span className="tag tag--quiet">{t('session.you')}</span>}
                {player.reentry && (
                  <span className="tag tag--quiet">
                    {t('session.reentry', { n: player.reentry.hand, to: player.reentry.to })}
                  </span>
                )}
                {player.eliminated && (
                  <span className="tag">
                    {player.eliminatedAtHand !== null
                      ? t('session.outAt', { n: player.eliminatedAtHand })
                      : t('session.out')}
                  </span>
                )}
              </span>
              <span className="board__bar" aria-hidden="true">
                <span
                  className="board__fill"
                  style={{ '--fill': `${(player.score / worst) * 100}%` } as React.CSSProperties}
                />
              </span>
              <span className="board__score">{formatNumber(player.score)}</span>
              <span className="board__chevron" aria-hidden="true" />
            </button>

            {expanded && (
              <dl className="micro">
                <Micro label={t('session.stat.won')} value={formatNumber(player.handsWon)} />
                <Micro
                  label={t('session.stat.average')}
                  value={
                    player.average === null
                      ? '·'
                      : formatNumber(Math.round(player.average * 10) / 10)
                  }
                />
                <Micro
                  label={t('session.stat.best')}
                  value={player.best === null ? '·' : formatNumber(player.best)}
                />
                <Micro label={t('session.stat.drops')} value={formatNumber(player.drops)} />
              </dl>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function Micro({ label, value }: { label: string; value: string }) {
  return (
    <div className="micro__cell">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/* ------------------------------------------------------------------ chart */

const CHART_W = 640;
const CHART_H = 250;
const PAD_X = 26;
const PAD_TOP = 18;
const PAD_BOTTOM = 30;

/**
 * How the lead moved, hand by hand.
 *
 * Scrubbing is the point: the line alone says "someone pulled away", the
 * readout says who and when. The SVG is hidden from assistive technology and
 * the same figures are announced through the readout, which is live — and the
 * hand-by-hand grid next door is the full table.
 */
function ScoreChart({ stats, seats, accountId }: BoardProps) {
  const { t, formatNumber } = useI18n();
  const [cursor, setCursor] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const columns = stats.hands;
  const at = cursor ?? columns;

  const x = useCallback(
    (i: number) => PAD_X + (columns === 0 ? 0 : (i / columns) * (CHART_W - PAD_X * 2)),
    [columns],
  );
  const y = useCallback(
    (v: number) => CHART_H - PAD_BOTTOM - (v / stats.peak) * (CHART_H - PAD_TOP - PAD_BOTTOM),
    [stats.peak],
  );

  const track = (event: React.PointerEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg || columns === 0) return;
    // The SVG keeps its aspect ratio, so a horizontal ratio of the rendered box
    // is the same ratio of the viewBox — no transform matrix needed.
    const box = svg.getBoundingClientRect();
    const vx = ((event.clientX - box.left) / box.width) * CHART_W;
    const index = ((vx - PAD_X) / (CHART_W - PAD_X * 2)) * columns;
    setCursor(Math.max(0, Math.min(columns, Math.round(index))));
  };

  const nudge = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const next = (cursor ?? columns) + (event.key === 'ArrowRight' ? 1 : -1);
    setCursor(Math.max(0, Math.min(columns, next)));
  };

  if (columns === 0) return <p className="muted">{t('session.chart.empty')}</p>;

  return (
    <div
      className="chart"
      role="group"
      tabIndex={0}
      aria-label={t('session.chart.a11y')}
      onKeyDown={nudge}
    >
      <svg
        ref={svgRef}
        className="chart__svg"
        viewBox={`0 0 ${CHART_W} ${CHART_H}`}
        aria-hidden="true"
        onPointerMove={track}
        onPointerDown={track}
        onPointerLeave={() => setCursor(null)}
      >
        {/* Zero and the highest score anyone reached: two references are enough
            to read every line between them. */}
        <line className="chart__axis" x1={PAD_X} x2={CHART_W - PAD_X} y1={y(0)} y2={y(0)} />
        <line
          className="chart__grid"
          x1={PAD_X}
          x2={CHART_W - PAD_X}
          y1={y(stats.peak)}
          y2={y(stats.peak)}
        />
        <text className="chart__tick" x={PAD_X} y={y(stats.peak) - 6}>
          {formatNumber(stats.peak)}
        </text>
        <line
          className="chart__cursor"
          x1={x(at)}
          x2={x(at)}
          y1={PAD_TOP - 6}
          y2={y(0) + 6}
        />

        {stats.players.map((player, index) => {
          const path = player.series
            .slice(0, columns + 1)
            .map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`)
            .join(' ');
          return (
            <g
              key={player.id}
              className={`chart__series chart__series--${index % 6}${
                player.id === accountId ? ' is-you' : ''
              }${player.rank === 1 ? ' is-winner' : ''}`}
            >
              <path className="chart__line" d={path} />
              <circle className="chart__dot" cx={x(at)} cy={y(player.series[at] ?? 0)} r={4.5} />
            </g>
          );
        })}

        {Array.from({ length: columns + 1 }, (_, i) => i)
          .filter((i) => columns <= 8 || i % Math.ceil(columns / 8) === 0 || i === columns)
          .map((i) => (
            <text key={i} className="chart__tick" x={x(i)} y={CHART_H - 8} textAnchor="middle">
              {i}
            </text>
          ))}
      </svg>

      <p className="chart__caption" aria-hidden="true">
        {at === 0 ? t('session.chart.start') : t('session.chart.hand', { n: at })}
      </p>

      <ul className="legend" aria-live="polite">
        {stats.players.map((player, index) => (
          <li
            key={player.id}
            className={`legend__item legend__item--${index % 6}${
              player.id === accountId ? ' is-you' : ''
            }`}
          >
            <span className="legend__swatch" aria-hidden="true" />
            <span className="legend__name">{seats.get(player.id)?.handle ?? player.id}</span>
            <span className="legend__value">{formatNumber(player.series[at] ?? 0)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------------------------------------------------------------- hand grid */

/** Every hand, every player, with the outcome carried by the cell itself. */
function HandGrid({ stats, seats, accountId }: BoardProps) {
  const { t, formatNumber } = useI18n();

  return (
    <div className="grid-scroll">
      <table className="standings standings--grid">
        <thead>
          <tr>
            <th scope="col">{t('session.hand')}</th>
            {stats.players.map((player) => (
              <th key={player.id} scope="col" className={player.id === accountId ? 'is-you' : ''}>
                {seats.get(player.id)?.handle ?? player.id}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: stats.hands }, (_, hand) => (
            <tr key={hand}>
              <th scope="row">{hand + 1}</th>
              {stats.players.map((player) => {
                const result = player.perHand[hand];
                if (!result) return <td key={player.id} className="cell cell--absent">·</td>;
                return (
                  <td
                    key={player.id}
                    className={`cell cell--${result.outcome}`}
                    title={t(`outcome.${result.outcome}` as MessageKey)}
                  >
                    {formatNumber(result.points)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">{t('summary.total')}</th>
            {stats.players.map((player) => (
              <td key={player.id} className={player.rank === 1 ? 'is-winner' : ''}>
                {formatNumber(player.score)}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
      {/* A re-entry resets a score, so that column genuinely does not add up to
          the figure under it. Saying so is the difference between a rule and a
          suspected bug. */}
      {stats.players.some((player) => player.reentry) && (
        <p className="muted muted--small">{t('session.reentry.note')}</p>
      )}
    </div>
  );
}
